// components/dashboardcomponents/RecentOrders.js
import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaHourglass,
  FaShoppingCart,
  FaTimesCircle,
} from "react-icons/fa";
import dayjs from "dayjs";
import Loader from "../Loader";

/**
 * ✅ RecentOrders — UI/UX Upgrade (logic kept same)
 * - Professional header + summary chips
 * - Search + status filter
 * - Sticky table header inside scroll container
 * - Better empty/error states
 * - Clean consistent typography + spacing (same family as your dashboard)
 */

const filterOldTracking = (orders = []) => {
  const tenDaysAgo = dayjs().subtract(10, "days");
  return (orders || []).map((order) => {
    const filteredTracking = (order.tracking || []).filter((track) => {
      const trackDate = dayjs(track.createdAt);
      return trackDate.isAfter(tenDaysAgo);
    });
    return { ...order, tracking: filteredTracking };
  });
};

const getLatestStatus = (tracker) => {
  const arr = tracker?.statusUpdates || [];
  return arr.length ? arr[arr.length - 1]?.status : "Unknown";
};

const RecentOrders = ({ orders = [], error, loading }) => {
  const [filterorders, setfilterorders] = useState([]);

  // UI state
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | Pending | Confirmed | Shipped | Delivered | Cancelled

  const renderStatusIcon = (status) => {
    switch (status) {
      case "Delivered":
        return <FaCheckCircle className="text-green-600" />;
      case "Pending":
        return <FaHourglass className="text-yellow-500" />;
      case "Confirmed":
        return <FaCheckCircle className="text-blue-600" />;
      case "Shipped":
        return <FaShoppingCart className="text-orange-500" />;
      case "Cancelled":
        return <FaTimesCircle className="text-red-600" />;
      default:
        return <FaTimesCircle className="text-gray-400" />;
    }
  };

  const statusPillClasses = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-50 text-green-700 border-green-200";
      case "Pending":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "Confirmed":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Shipped":
        return "bg-orange-50 text-orange-700 border-orange-200";
      case "Cancelled":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  useEffect(() => {
    setfilterorders(filterOldTracking(orders));
    // ✅ keep your dependency style but safer than orders.length only
  }, [orders]);

  // Flatten tracking to table rows (keeps your data mapping same, just organized)
  const rows = useMemo(() => {
    const out = [];
    (filterorders || []).forEach((order) => {
      (order?.tracking || []).forEach((tracker) => {
        const productId = tracker?.statusUpdates?.[0]?.productId;
        const product = (order?.items || []).find(
          (p) => p?.product?._id === productId
        );

        out.push({
          key: tracker?._id || `${order?._id}-${productId}`,
          tracker,
          order,
          product,
          status: getLatestStatus(tracker),
          createdAt: tracker?.createdAt,
        });
      });
    });
    return out;
  }, [filterorders]);

  const stats = useMemo(() => {
    const total = rows.length;
    const byStatus = rows.reduce((acc, r) => {
      const s = r.status || "Unknown";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    return { total, byStatus };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();

    return rows
      .filter((r) => {
        const matchQuery =
          !q ||
          String(r?.tracker?._id || "")
            .toLowerCase()
            .includes(q) ||
          String(r?.product?.product?.name || "")
            .toLowerCase()
            .includes(q) ||
          String(r?.product?.product?.category?.name || "")
            .toLowerCase()
            .includes(q) ||
          String(r?.product?.product?.subcategory?.name || "")
            .toLowerCase()
            .includes(q);

        const matchStatus =
          statusFilter === "all" ? true : r.status === statusFilter;

        return matchQuery && matchStatus;
      })
      .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
  }, [rows, query, statusFilter]);

  const STATUS_OPTIONS = ["all", "Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];

  if (loading) {
    return (
      <div className="h-[340px] bg-gray-50 p-6 rounded-xl shadow-lg mb-10 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 shadow-xl rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600">
                  <FaShoppingCart />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Recent Deals
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Latest tracking updates from the last 10 days.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 bg-white text-gray-700">
                Total: {stats.total}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700">
                Delivered: {stats.byStatus?.Delivered || 0}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-yellow-200 bg-yellow-50 text-yellow-700">
                Pending: {stats.byStatus?.Pending || 0}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-orange-200 bg-orange-50 text-orange-700">
                Shipped: {stats.byStatus?.Shipped || 0}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex-1">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search order id, product, category..."
                className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === "all" ? "All Status" : s}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  setQuery("");
                  setStatusFilter("all");
                }}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 transition"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
              Once you receive orders, they’ll show up here!
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="max-h-[460px] overflow-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-gray-200">
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Product
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Order / Tracking
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Category
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Status
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Date
              </th>
            </tr>
          </thead>

          <tbody className="bg-gray-50">
            {!error && filteredRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-10">
                  <div className="text-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-blue-600">
                      <FaShoppingCart />
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-gray-900">
                      No recent tracking updates
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Try changing the status filter or clearing search.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => {
                const product = r?.product?.product;
                const tracker = r?.tracker;
                const status = r?.status;

                return (
                  <tr
                    key={r.key}
                    className="border-b border-gray-200 hover:bg-gray-100 transition"
                  >
                    {/* Product */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3 min-w-[260px]">
                        <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 overflow-hidden flex items-center justify-center">
                          {product?.image ? (
                            <img
                              src={`data:${product?.image?.contentType};base64,${product?.image?.data}`}
                              alt={product?.name || "Product"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-gray-500">No Image</span>
                          )}
                        </div>

                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {product?.name || "Product Not Found"}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            Sub: {product?.subcategory?.name || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* IDs */}
                    <td className="px-5 py-4">
                      <div className="text-sm text-gray-900 font-semibold">
                        {tracker?._id?.length > 12
                          ? `${tracker._id.slice(0, 12)}...`
                          : tracker?._id || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Tracking ID
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-5 py-4">
                      <div className="text-sm text-gray-900">
                        {product?.category?.name || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Sub: {product?.subcategory?.name || "N/A"}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${statusPillClasses(
                          status
                        )}`}
                      >
                        {renderStatusIcon(status)}
                        {status}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="px-5 py-4">
                      <div className="text-sm text-gray-900">
                        {r?.createdAt
                          ? dayjs(r.createdAt).format("DD-MMM-YY")
                          : "NA"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r?.createdAt
                          ? dayjs(r.createdAt).format("HH:mm:ss")
                          : ""}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentOrders;